import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { useAuth } from '../contexts/AuthContext'
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"

import { Progress } from "./ui/progress"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarNav,
  SidebarNavItem,
} from "./ui/sidebar"
import {
  Calendar,
  Cloud,
  Droplets,
  Home,
  Leaf,
  Settings,
  Sun,
  Thermometer,
  User,
  CheckCircle2,
  AlertTriangle,
  Sprout,
  TreePine,
  LogOut,
  Menu,
  X,
  Heart,
} from "lucide-react"

export default function Dashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isAuthenticated } = useAuth()
  const [sidebarOpen, setSidebarOpen] = React.useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, right: 0 })
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  // Close dropdown when clicking outside and calculate position
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    
    // Calculate dropdown position when it opens
    if (isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px margin
        right: window.innerWidth - rect.right
      })
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDropdownOpen])

  // Show loading state while authentication is being checked
  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700">Kraunama...</p>
        </div>
      </div>
    )
  }

  // Error handling for translations
  const safeTranslate = (key: string, options?: any): string => {
    try {
      const result = t(key, options)
      return typeof result === 'string' ? result : key
    } catch (error) {
      console.error(`Translation error for key: ${key}`, error)
      return key
    }
  }

  const navigationItems = [
    {
      title: "Pagrindinis",
      description: "Pagrindinis skydelis",
      url: '/dashboard',
      icon: Home
    },
    {
      title: "Mano Sodas",
      description: "Jūsų augalų sąrašas",
      url: '/my-plants',
      icon: Heart
    },
    {
      title: "Augalų Katalogas",
      description: "Augalų duomenų bazė",
      url: '/plants',
      icon: Leaf
    },
    {
      title: "Užduotys",
      description: "Priežiūros užduotys",
      url: '/tasks',
      icon: CheckCircle2
    },
    {
      title: "Kalendorius",
      description: "Priežiūros kalendorius",
      url: '/calendar',
      icon: Calendar
    },
    {
      title: "Orai",
      description: "Oro prognozės",
      url: '/weather',
      icon: Cloud
    }
  ]

  const quickStats = [
    {
      title: t('dashboard.stats.plants.title'),
      value: "24",
      description: t('dashboard.stats.plants.description'),
      icon: Sprout,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      title: t('dashboard.stats.tasks.title'),
      value: "7",
      description: t('dashboard.stats.tasks.description'),
      icon: Droplets,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      title: t('dashboard.stats.healthy.title'),
      value: "22",
      description: t('dashboard.stats.healthy.description'),
      icon: TreePine,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      title: t('dashboard.stats.attention.title'),
      value: "2",
      description: t('dashboard.stats.attention.description'),
      icon: AlertTriangle,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
  ]

  const recentTasks = [
    { 
      plant: "Monstera Deliciosa", 
      task: t('dashboard.tasks.watering'), 
      time: t('dashboard.tasks.time.2hoursAgo'), 
      status: "completed" 
    },
    { 
      plant: "Ficus Benjamina", 
      task: t('dashboard.tasks.fertilizing'), 
      time: t('dashboard.tasks.time.today2pm'), 
      status: "pending" 
    },
    { 
      plant: "Sansevieria", 
      task: t('dashboard.tasks.repotting'), 
      time: t('dashboard.tasks.time.tomorrow'), 
      status: "upcoming" 
    },
  ]

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const handleNavigation = (url: string) => {
    navigate(url)
    setMobileSidebarOpen(false)
  }

  const getUserInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const isActiveRoute = (url: string) => {
    return location.pathname === url
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
      {/* Desktop Sidebar */}
      <Sidebar className={`hidden lg:flex w-64 flex-col bg-white/80 backdrop-blur-sm border-r border-emerald-100 shadow-2xl transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        {/* Sidebar Header */}
        <SidebarHeader className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
              <Leaf className="h-5 w-5" />
            </div>
            {sidebarOpen && (
              <div className="flex flex-col">
                <span className="text-lg font-bold text-emerald-800">{t('branding.appName')}</span>
                <span className="text-xs text-emerald-600">{t('dashboard.sidebar.subtitle')}</span>
              </div>
            )}
          </div>
        </SidebarHeader>
        
        {/* Navigation Content */}
        <SidebarContent className="flex-1 bg-white/80 backdrop-blur-sm px-3 py-4">
          <SidebarNav className="space-y-1">
            {navigationItems.map((item) => {
              const isActive = isActiveRoute(item.url)
              return (
                <SidebarNavItem
                  key={item.title}
                  active={isActive}
                  className={`
                    group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                    ${isActive 
                      ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                      : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    }
                  `}
                  onClick={() => handleNavigation(item.url)}
                >
                  <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                  {sidebarOpen && (
                    <div className="flex flex-col">
                      <span>{item.title}</span>
                      <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                    </div>
                  )}
                </SidebarNavItem>
              )
            })}
          </SidebarNav>
        </SidebarContent>
        
        {/* Sidebar Footer */}
        <SidebarFooter className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
          <SidebarNav>
            <SidebarNavItem 
              className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 cursor-pointer"
            >
              <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
              {sidebarOpen && (
                <div className="flex flex-col">
                  <span>{t('dashboard.navigation.settings')}</span>
                  <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                </div>
              )}
            </SidebarNavItem>
          </SidebarNav>
        </SidebarFooter>
      </Sidebar>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileSidebarOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-64 bg-white/80 backdrop-blur-sm shadow-2xl">
            <div className="flex h-full flex-col">
              {/* Mobile Header */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
                      <Leaf className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-lg font-bold text-emerald-800">{t('branding.appName')}</span>
                      <span className="text-xs text-emerald-600">{t('dashboard.sidebar.subtitle')}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-emerald-700 hover:bg-emerald-100"
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="flex-1 px-3 py-4">
                <SidebarNav className="space-y-1">
                  {navigationItems.map((item) => {
                    const isActive = isActiveRoute(item.url)
                    return (
                      <SidebarNavItem
                        key={item.title}
                        active={isActive}
                        className={`
                          group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                          ${isActive 
                            ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }
                        `}
                        onClick={() => handleNavigation(item.url)}
                      >
                        <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                        <div className="flex flex-col">
                          <span>{item.title}</span>
                          <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                        </div>
                      </SidebarNavItem>
                    )
                  })}
                </SidebarNav>
              </div>

              {/* Mobile Footer */}
              <div className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
                <SidebarNav>
                  <SidebarNavItem 
                    href="#" 
                    className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200"
                  >
                    <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
                    <div className="flex flex-col">
                      <span>{t('dashboard.navigation.settings')}</span>
                      <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                    </div>
                  </SidebarNavItem>
                </SidebarNav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-emerald-100 bg-white/80 backdrop-blur-sm px-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-700 hover:bg-emerald-50 lg:hidden"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-700 hover:bg-emerald-50 hidden lg:flex"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="h-4 w-px bg-emerald-200" />
            <h1 className="text-lg font-semibold text-emerald-800">{t('dashboard.navigation.home')}</h1>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 text-sm text-emerald-700">
              <Thermometer className="h-4 w-4" />
              <span>22°C</span>
              <Sun className="h-4 w-4 ml-2" />
              <span>{t('dashboard.weather.sunny')}</span>
            </div>

            <div className="relative dropdown-container" ref={dropdownRef}>
              <Button 
                ref={buttonRef}
                variant="ghost" 
                className="relative h-8 w-8 rounded-full hover:bg-emerald-50"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <Avatar className="h-8 w-8 border-2 border-emerald-200">
                  <AvatarImage src="/placeholder.svg?height=32&width=32" alt="User avatar" />
                  <AvatarFallback className="bg-emerald-100 text-emerald-700">
                    {user ? getUserInitials(user.full_name) : 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 space-y-6 p-4 md:p-6 overflow-auto">
          {/* Welcome Section */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 to-green-600 p-6 text-white shadow-2xl">
            <div className="relative z-10">
              <h2 className="text-2xl font-bold mb-2">
                {safeTranslate('dashboard.welcome.title', { 
                  name: user?.full_name ? user.full_name.split(' ')[0] : 'Vartotojau'
                })}
              </h2>
              <p className="text-emerald-100 mb-4">{safeTranslate('dashboard.welcome.subtitle')}</p>
              <Button 
                variant="secondary" 
                className="bg-white text-emerald-700 hover:bg-emerald-50"
                onClick={() => navigate('/tasks')}
              >
                {safeTranslate('dashboard.welcome.viewTasks')}
              </Button>
            </div>
            <div className="absolute right-0 top-0 h-full w-32 opacity-10">
              <div className="h-full w-full bg-[url('data:image/svg+xml,%3Csvg width=60 height=60 viewBox=0 0 60 60 xmlns=http://www.w3.org/2000/svg%3E%3Cg fill=none fillRule=evenodd%3E%3Cg fill=%23ffffff fillOpacity=0.1%3E%3Cpath d=M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] bg-repeat" />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {quickStats.map((stat, index) => (
              <Card
                key={index}
                className="border-0 shadow-2xl hover:shadow-3xl transition-shadow duration-200 bg-white/80 backdrop-blur-sm"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recent Activity & Weather */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Recent Tasks */}
            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-emerald-800">{t('dashboard.recentTasks.title')}</CardTitle>
                <CardDescription>{t('dashboard.recentTasks.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentTasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-emerald-50/50 hover:bg-emerald-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          task.status === "completed"
                            ? "bg-emerald-500"
                            : task.status === "pending"
                              ? "bg-amber-500"
                              : "bg-emerald-400"
                        }`}
                      />
                      <div>
                        <p className="font-medium text-sm text-gray-900">{task.plant}</p>
                        <p className="text-xs text-gray-500">{task.task}</p>
                      </div>
                    </div>
                    <Badge
                      variant={
                        task.status === "completed" ? "default" : task.status === "pending" ? "secondary" : "outline"
                      }
                      className="text-xs"
                    >
                      {task.time}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Weather Widget */}
            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-emerald-800">{t('dashboard.weather.title')}</CardTitle>
                <CardDescription>{t('dashboard.weather.location')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Sun className="h-8 w-8 text-amber-500" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900">22°C</p>
                      <p className="text-sm text-gray-500">{t('dashboard.weather.sunny')}</p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>{t('dashboard.weather.humidity')}: 65%</p>
                    <p>{t('dashboard.weather.wind')}: 12 km/h</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('dashboard.weather.uvIndex')}</span>
                    <span className="font-medium">6/10</span>
                  </div>
                  <Progress value={60} className="h-2" />
                </div>

                <div className="pt-2 border-t border-emerald-100">
                  <p className="text-xs text-emerald-700 font-medium">{t('dashboard.weather.recommendation')}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
      
      {/* Portal Dropdown */}
      {isDropdownOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`
          }}
        >
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-900">
              {user ? user.full_name : 'Nežinomas vartotojas'}
            </p>
            <p className="text-xs text-gray-500">
              {user ? user.email : ''}
            </p>
          </div>
          
          <div className="py-1">
            <div className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 cursor-pointer">
              <User className="w-4 h-4" />
              <span>Profilis</span>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 cursor-pointer">
              <Settings className="w-4 h-4" />
              <span>Nustatymai</span>
            </div>
          </div>
          
          <div className="border-t border-gray-200 py-1">
            <div 
              className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
              onClick={() => {
                setIsDropdownOpen(false)
                handleLogout()
              }}
            >
              <LogOut className="w-4 h-4" />
              <span>Atsijungti</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
} 